/**
 * The predicted search area, drawn on the map.
 *
 * A commander reads this before sending anyone into a field, so the drawing
 * carries the same honesty the estimate does. There are two things on the map,
 * and they do different jobs:
 *
 * - The **heat map** is the Monte Carlo cloud — thousands of simulated falls,
 *   drawn as density. It shows where the aircraft probably is, and how tightly
 *   or loosely the physics pins that down. It is a texture, not a ruler.
 * - The **containment rings** are the ruler. 50% and 90% of the simulated
 *   impacts fall inside them, they are real geodesic circles at true ground
 *   scale, and they are what a search is actually planned against.
 *
 * The plain scenario rings (return-to-launch, flyaway) are still drawn for the
 * cases that do not scatter into a cloud. The loss ring is dropped when a heat
 * map is present, because the cloud says the same thing with more detail.
 */

import { useEffect } from 'react';

import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { circleRing, movePoint } from '@/map/useZoneLayer';
import { useImpactStore, type ImpactEstimate, type ImpactResult } from '@/stores/impactStore';

export const IMPACT_SOURCE_ID = 'impact-rings';
export const IMPACT_HEAT_SOURCE_ID = 'impact-heat';
export const IMPACT_CONTOUR_SOURCE_ID = 'impact-contours';
const IMPACT_FILL_LAYER = 'impact-rings-fill';
const IMPACT_LINE_LAYER = 'impact-rings-line';
const IMPACT_LABEL_LAYER = 'impact-rings-label';
const IMPACT_HEAT_LAYER = 'impact-heat-layer';
const IMPACT_CONTOUR_LINE_LAYER = 'impact-contour-line';
const IMPACT_CONTOUR_LABEL_LAYER = 'impact-contour-label';

const COLOR: Record<ImpactEstimate['likelihood'], string> = {
  primary: '#f0563f',
  secondary: '#f0a53f',
  possible: '#8a94a6',
};

const OPACITY: Record<ImpactEstimate['likelihood'], number> = {
  primary: 0.22,
  secondary: 0.14,
  possible: 0.08,
};

const SCENARIO_LABEL: Record<ImpactEstimate['scenario'], string> = {
  loss: 'LOSS',
  return: 'RTL',
  continue: 'FLYAWAY',
};

export function impactFeature(estimate: ImpactEstimate): unknown {
  return {
    type: 'Feature',
    properties: {
      color: COLOR[estimate.likelihood],
      fillOpacity: OPACITY[estimate.likelihood],
      label: `${SCENARIO_LABEL[estimate.scenario]} · ${Math.round(estimate.radiusM)} m`,
    },
    geometry: {
      type: 'Polygon',
      coordinates: [circleRing(estimate.lat, estimate.lon, estimate.radiusM)],
    },
  };
}

/** The scenario rings to draw: skip the loss ring when a cloud supersedes it. */
export function ringFeatures(result: ImpactResult): unknown[] {
  const hasCloud = result.heatmap !== null;
  return result.estimates
    .filter((estimate) => !(hasCloud && estimate.scenario === 'loss'))
    .map(impactFeature);
}

export function heatFeatures(result: ImpactResult): unknown[] {
  return (result.heatmap?.points ?? []).map((point) => ({
    type: 'Feature',
    properties: {},
    geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
  }));
}

/** Containment rings as true-scale circles, labelled with their percentage. */
export function contourFeatures(result: ImpactResult): unknown[] {
  const contours = result.heatmap?.contours ?? [];
  return contours.flatMap((contour) => {
    const [labelLon, labelLat] = movePoint(contour.lat, contour.lon, 0, contour.radiusM);
    return [
      {
        type: 'Feature',
        properties: { pct: `${Math.round(contour.fraction * 100)}%` },
        geometry: {
          type: 'Polygon',
          coordinates: [circleRing(contour.lat, contour.lon, contour.radiusM)],
        },
      },
      {
        type: 'Feature',
        properties: { pct: `${Math.round(contour.fraction * 100)}%` },
        geometry: { type: 'Point', coordinates: [labelLon, labelLat] },
      },
    ];
  });
}

export interface ImpactLayer {
  destroy: () => void;
  stats: () => { redraws: number; rings: number; heatPoints: number; contours: number };
}

export function createImpactLayer(map: MapLike): ImpactLayer {
  let redraws = 0;
  let rings = 0;
  let heatPoints = 0;
  let contourCount = 0;

  for (const id of [IMPACT_SOURCE_ID, IMPACT_HEAT_SOURCE_ID, IMPACT_CONTOUR_SOURCE_ID]) {
    if (!map.getSource(id)) {
      map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    }
  }

  // Heat map underneath everything, so the rings and labels read on top of it.
  if (!map.getLayer(IMPACT_HEAT_LAYER)) {
    map.addLayer({
      id: IMPACT_HEAT_LAYER,
      type: 'heatmap',
      source: IMPACT_HEAT_SOURCE_ID,
      paint: {
        'heatmap-weight': 1,
        'heatmap-intensity': 0.9,
        // Transparent where nothing landed, up through amber to red where the
        // cloud is densest — the "weather radar" reading a commander expects.
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(0,0,0,0)',
          0.2,
          'rgba(63,169,245,0.5)',
          0.5,
          'rgba(240,165,63,0.7)',
          1,
          'rgba(240,86,63,0.9)',
        ],
        'heatmap-radius': 22,
        'heatmap-opacity': 0.75,
      },
    });
  }

  if (!map.getLayer(IMPACT_FILL_LAYER)) {
    map.addLayer({
      id: IMPACT_FILL_LAYER,
      type: 'fill',
      source: IMPACT_SOURCE_ID,
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['get', 'fillOpacity'] },
    });
  }

  if (!map.getLayer(IMPACT_LINE_LAYER)) {
    map.addLayer({
      id: IMPACT_LINE_LAYER,
      type: 'line',
      source: IMPACT_SOURCE_ID,
      paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
    });
  }

  if (!map.getLayer(IMPACT_LABEL_LAYER)) {
    map.addLayer({
      id: IMPACT_LABEL_LAYER,
      type: 'symbol',
      source: IMPACT_SOURCE_ID,
      layout: { 'text-field': ['get', 'label'], 'text-size': 11 },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  if (!map.getLayer(IMPACT_CONTOUR_LINE_LAYER)) {
    map.addLayer({
      id: IMPACT_CONTOUR_LINE_LAYER,
      type: 'line',
      source: IMPACT_CONTOUR_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'line-color': '#ffffff', 'line-width': 1.5, 'line-dasharray': [2, 2] },
    });
  }

  if (!map.getLayer(IMPACT_CONTOUR_LABEL_LAYER)) {
    map.addLayer({
      id: IMPACT_CONTOUR_LABEL_LAYER,
      type: 'symbol',
      source: IMPACT_CONTOUR_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: { 'text-field': ['get', 'pct'], 'text-size': 11 },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const draw = (result: ImpactResult | null) => {
    const ringSource = geoJsonSource(map, IMPACT_SOURCE_ID);
    const heatSource = geoJsonSource(map, IMPACT_HEAT_SOURCE_ID);
    const contourSource = geoJsonSource(map, IMPACT_CONTOUR_SOURCE_ID);
    const ringData = result ? ringFeatures(result) : [];
    const heatData = result ? heatFeatures(result) : [];
    const contourData = result ? contourFeatures(result) : [];
    ringSource?.setData({ type: 'FeatureCollection', features: ringData });
    heatSource?.setData({ type: 'FeatureCollection', features: heatData });
    contourSource?.setData({ type: 'FeatureCollection', features: contourData });
    rings = ringData.length;
    heatPoints = heatData.length;
    contourCount = contourData.length;
    redraws += 1;
  };

  const unsubscribe = useImpactStore.subscribe((state) => draw(state.result));
  draw(useImpactStore.getState().result);

  return {
    destroy: () => {
      unsubscribe();
      for (const id of [
        IMPACT_CONTOUR_LABEL_LAYER,
        IMPACT_CONTOUR_LINE_LAYER,
        IMPACT_LABEL_LAYER,
        IMPACT_LINE_LAYER,
        IMPACT_FILL_LAYER,
        IMPACT_HEAT_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of [IMPACT_SOURCE_ID, IMPACT_HEAT_SOURCE_ID, IMPACT_CONTOUR_SOURCE_ID]) {
        if (map.getSource(id)) map.removeSource(id);
      }
    },
    stats: () => ({ redraws, rings, heatPoints, contours: contourCount }),
  };
}

export function useImpactLayer(): void {
  const controller = useMapController();
  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createImpactLayer(map);
        return () => layer.destroy();
      }),
    [controller],
  );
}
